import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  Clock,
  FolderGit2,
  Laptop,
  Layers,
  type LucideIcon,
  ScrollText,
  ShieldCheck,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { ClaudeMark } from '@/components/common/marks';
import { Page, ViewHeader } from '@/components/layout/Page';
import { StatTile } from '@/features/dashboard/StatTile';
import { useHealth, useInvites, useStats, useTasks, useUsers, useWorkers } from '@/hooks/use-fleet';
import { presenceOf } from '@/lib/fleet';
import { cn } from '@/lib/utils';

/**
 * The admin section: one page with three tabs, where there were three pages in
 * the rail.
 *
 * What this replaces was a nav group of three links -- Overview, People, Audit
 * log -- opening three unrelated-looking screens, one of which was a header, a
 * grid of four figures, and then two more grids of figures under headings. Three
 * rows of statistics down an empty 1100px column is not a dashboard; it is a
 * report with the paragraphs removed.
 *
 * Now every admin screen opens with the same five figures -- the five an
 * operator is actually checking -- and the tabs are on the page, beside them,
 * where a person can see all three at once and see which one they are on. The
 * routes did not change: `/admin`, `/admin/people` and `/admin/audit` are still
 * three URLs and still three server-gated reads, so a link somebody saved still
 * lands where it did and the refusal is still the server's.
 *
 * The figure strip opened on the brand gradient (`dy-hero`) until issue #87: a
 * fixed-dark panel that did not answer to the theme, so light mode saw the same
 * near-black band the dashboard's own hero used to draw before issue #67 fixed
 * it there. It is `dy-surface-2` now, the themed surface every other opening
 * band in the console uses, and each figure is a `StatTile` -- the same
 * primitive the dashboard's own band and the account card draw with -- rather
 * than a bespoke column. `StatTile` without a `to` renders as a plain,
 * unclickable surface: these five report, they do not navigate.
 */

interface Tab {
  to: string;
  label: string;
  icon: LucideIcon;
  exact: boolean;
}

const TABS: Tab[] = [
  { to: '/admin', label: 'Overview', icon: ShieldCheck, exact: true },
  { to: '/admin/people', label: 'People', icon: Users, exact: false },
  { to: '/admin/repos', label: 'Repos', icon: FolderGit2, exact: false },
  { to: '/admin/audit', label: 'Audit log', icon: ScrollText, exact: false },
];

export function AdminShell({
  tab,
  actions,
  children,
}: {
  /** Which tab is lit. Passed rather than derived so a route owns its own tab. */
  tab: 'overview' | 'people' | 'repos' | 'audit';
  /** The page's own primary verb, in the header. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const health = useHealth();
  const stats = useStats();
  const workers = useWorkers();
  const tasks = useTasks();
  const users = useUsers();
  const invites = useInvites();

  const workerRows = workers.data ?? [];
  const online = workerRows.filter((worker) => presenceOf(worker) !== 'offline').length;
  const queued = stats.data?.queue.queued ?? 0;
  const inFlight = stats.data?.tasks.inFlight ?? 0;
  const failed = (tasks.data ?? []).filter((task) => task.status === 'failed').length;
  const people = users.data ?? [];
  const admins = people.filter((person) => person.isAdmin).length;
  const openInvites = (invites.data ?? []).filter((invite) => invite.status === 'open').length;
  const warnings = health.data?.warnings?.length ?? 0;

  // A queue with nothing to run it is the failure this section exists to catch,
  // and it is invisible in any single number: zero online devices is fine on a
  // Sunday and is an outage when six tasks are queued.
  const stranded = queued > 0 && online === 0;

  return (
    <>
      <ViewHeader
        title="Admin"
        icon={ShieldCheck}
        sub="health, capacity, intake, access and the full record"
        width="1180px"
        actions={actions}
      />
      <Page className="max-w-[1180px]">
        <div className="dy-stack">
          <section
            className="dy-surface-2 px-5 py-5 sm:px-7 sm:py-6"
            aria-label="The hub at a glance"
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <StatTile
                label="People"
                icon={Users}
                tone="var(--dy-fg-2)"
                value={people.length}
                note={`${admins} admin${admins === 1 ? '' : 's'} · ${openInvites} invite${
                  openInvites === 1 ? '' : 's'
                } open`}
              />
              <StatTile
                label="Devices online"
                icon={Laptop}
                tone="var(--dy-info)"
                alarm={stranded}
                value={`${online}/${workerRows.length}`}
                note={`${stats.data?.workers.capacity ?? 0} concurrent slots`}
              />
              <StatTile
                label="Running"
                icon={Layers}
                tone="var(--dy-info)"
                value={inFlight}
                note="on somebody's laptop"
              />
              <StatTile
                label="Queued"
                icon={Clock}
                tone="var(--dy-fg-2)"
                alarm={stranded}
                value={queued}
                note={stranded ? 'nothing awake to run it' : 'waiting for a free device'}
              />
              <StatTile
                label="Failed"
                icon={AlertTriangle}
                tone="var(--dy-fg-2)"
                alarm={failed > 0}
                value={failed}
                note="still open, needing a decision"
              />
            </div>

            {warnings || stranded ? (
              <div
                className={cn(
                  'border-line mt-4 flex items-center gap-2.5 border-t pt-4 text-sm',
                  'text-warn',
                )}
              >
                <AlertTriangle className="size-4 shrink-0" aria-hidden />
                <span className="min-w-0 truncate">
                  {stranded
                    ? 'Work is queued and every enrolled device is offline.'
                    : `${warnings} configuration warning${warnings === 1 ? '' : 's'}${
                        tab === 'overview' ? ', under Hub health below.' : ' on the Overview tab.'
                      }`}
                </span>
              </div>
            ) : (
              <div className="border-line text-fg-2 mt-4 flex items-center gap-2.5 border-t pt-4 text-sm">
                <ClaudeMark className="size-4 shrink-0" />
                <span className="min-w-0 truncate">
                  Nothing is misconfigured. Agents run on people's own laptops, under their own
                  Claude subscriptions.
                </span>
              </div>
            )}
          </section>

          {/* The tabs. Links rather than state, because each one is a separate
              server-gated read and a separate URL somebody can send. */}
          <nav aria-label="Admin sections" className="flex min-w-0 flex-wrap items-center gap-1.5">
            {TABS.map((entry) => {
              const active =
                (entry.exact && tab === 'overview' && entry.to === '/admin') ||
                (!entry.exact && entry.to === `/admin/${tab}`);
              const Icon = entry.icon;
              return (
                <Link
                  key={entry.to}
                  to={entry.to}
                  className={cn(
                    'flex h-9 items-center gap-2 rounded-full px-3.5 text-base transition-colors duration-100',
                    active
                      ? 'dy-surface text-foreground font-medium'
                      : 'text-fg-2 hover:bg-raised hover:text-foreground',
                  )}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon className="size-4 opacity-70" aria-hidden />
                  {entry.label}
                </Link>
              );
            })}
          </nav>

          {children}
        </div>
      </Page>
    </>
  );
}
